import { Link } from '@tanstack/react-router';
import { CaretRight } from '@phosphor-icons/react';
import type { ProjectResponse } from '@mybusinessmate/schema';
import { DOMAIN_META, openTaskSummary } from '../../lib/project-display';
import { Icon } from '../Icon';
import { AvatarStack } from '../ui/avatar-stack';
import { ProgressBar } from '../ui/progress-bar';
import { Skeleton } from '../ui/skeleton';
import { DomainTile } from './domain-tile';

/** Columns of Figma A05.1 "Card · Table": Project 346, Progress 155, Tasks 166, Team 90, chevron. Below `lg`: project + chevron. */
export const PROJECT_ROW_GRID =
  'grid items-center gap-(--mbm-space-4) pl-(--mbm-space-5) pr-(--mbm-space-4) grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,346fr)_minmax(0,155fr)_minmax(0,166fr)_minmax(0,90fr)_auto]';

const HEAD = 'text-label-small text-text-tertiary uppercase';
const monthYear = new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });

export function ProjectRowsHeader() {
  return (
    <div
      className={`${PROJECT_ROW_GRID} hidden h-(--mbm-space-8) border-b border-border-line bg-surface-canvas lg:grid`}
    >
      <span className={HEAD}>Project</span>
      <span className={HEAD}>Progress</span>
      <span className={HEAD}>Tasks</span>
      <span className={HEAD}>Team</span>
      <span aria-hidden className="w-(--mbm-space-5)" />
    </div>
  );
}

/** The one line that says what needs a person, most pressing first. Null when nothing does. */
export function attentionLine(
  p: Pick<ProjectResponse, 'changesRequestedCount' | 'needsTriageCount' | 'waitingForClientCount'>,
) {
  if (p.changesRequestedCount > 0) return `Revision requested on ${p.changesRequestedCount}`;
  if (p.needsTriageCount > 0) return `${p.needsTriageCount} needs triage`;
  if (p.waitingForClientCount > 0) return `${p.waitingForClientCount} waiting for client`;
  return null;
}

/**
 * A project in the staff list (Figma A05.1).
 * TODO(api): the design's progress caption is "3 of 4 milestones" / "Oct cycle"; projects have no milestones or cycle,
 * so it counts tasks. "1 extra over quota" needs plan allowances (billing).
 */
export function ProjectRow({ project }: { project: ProjectResponse }) {
  const attention = attentionLine(project);
  return (
    <Link
      to="/subdomain/admin/projects/$projectId"
      params={{ projectId: project.id }}
      className={`${PROJECT_ROW_GRID} border-b border-border-line py-(--mbm-space-4) transition-colors duration-(--mbm-duration-fast) hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)`}
    >
      <span className="flex min-w-0 items-center gap-(--mbm-space-3)">
        <DomainTile domain={project.domain} variant="dark" />
        <span className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
          <span className="truncate text-label-large text-text-primary">{project.name}</span>
          <span className="truncate text-body-xsmall text-text-tertiary">
            {DOMAIN_META[project.domain].label} · since {monthYear.format(new Date(project.startDate))}
          </span>
        </span>
      </span>

      <span className="hidden flex-col gap-(--mbm-space-1-5) pr-(--mbm-space-4) lg:flex">
        <span className="flex items-baseline gap-(--mbm-space-2)">
          <span className="text-label-large text-text-primary">{project.progress}%</span>
          <span className="truncate text-body-xsmall text-text-tertiary">
            {project.taskCount === 0 ? 'No tasks yet' : `${project.doneTaskCount} of ${project.taskCount} tasks`}
          </span>
        </span>
        <ProgressBar size="slim" value={project.progress} tone="dark" label={`${project.name} progress`} />
      </span>

      <span className="hidden flex-col lg:flex">
        <span className="text-body-small text-text-primary">{openTaskSummary(project)}</span>
        {attention ? <span className="text-caption text-feedback-warning">{attention}</span> : null}
      </span>

      <span className="hidden lg:block">
        <AvatarStack names={project.team} />
      </span>

      <span className="text-text-muted">
        <Icon icon={CaretRight} size="md" />
      </span>
    </Link>
  );
}

export function ProjectRowSkeleton() {
  return (
    <div className={`${PROJECT_ROW_GRID} border-b border-border-line py-(--mbm-space-4)`}>
      <span className="flex items-center gap-(--mbm-space-3)">
        <Skeleton className="size-(--mbm-size-icon-tile-project) rounded-(--mbm-component-radius-icon-tile)" />
        <Skeleton className="h-(--mbm-space-2-5) w-1/2" />
      </span>
      {Array.from({ length: 3 }, (_, i) => (
        <span key={i} className="hidden lg:block">
          <Skeleton className="h-(--mbm-space-2-5) w-2/3" />
        </span>
      ))}
      <span />
    </div>
  );
}
